import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable
} from '@tanstack/react-table'
import { useMutation } from '@tanstack/react-query'
import { Columns2, ExternalLink, RotateCcw } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import { updateResultReview, type ReviewState, type SearchResult } from '../lib/api'

const columnHelper = createColumnHelper<SearchResult>()

type ResultsTableProps = {
  caseId: string | null
  results: SearchResult[]
  canEditReview: boolean
  onReviewUpdated: () => void
}

type StatusFilter = 'all' | 'live' | 'dead'
type ReviewFilter = 'all' | ReviewState
type SortMode = 'rank' | 'score' | 'status' | 'filing_date'

export function ResultsTable({
  caseId,
  results,
  canEditReview,
  onReviewUpdated
}: ResultsTableProps) {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [reviewFilter, setReviewFilter] = useState<ReviewFilter>('all')
  const [sortMode, setSortMode] = useState<SortMode>('rank')
  const [selectedResultId, setSelectedResultId] = useState<string | null>(null)
  const visibleResults = useMemo(
    () => sortResults(filterResults(results, statusFilter, reviewFilter), sortMode),
    [results, statusFilter, reviewFilter, sortMode]
  )
  const selectedResult =
    visibleResults.find((result) => result.id === selectedResultId) ?? visibleResults[0] ?? null
  const columns = [
    columnHelper.accessor('rank', {
      header: 'Rank',
      cell: (info) => info.getValue()
    }),
    columnHelper.accessor('trademark_serial_number', {
      header: 'Serial',
      cell: (info) => info.getValue()
    }),
    columnHelper.accessor('mark_literal', {
      header: 'Mark',
      cell: (info) => info.getValue() ?? 'Design mark'
    }),
    columnHelper.accessor('status_label', {
      header: 'Status',
      cell: (info) => <span className="statusPill">{info.getValue()}</span>
    }),
    columnHelper.accessor('international_classes', {
      header: 'Class',
      cell: (info) => info.getValue().join(', ')
    }),
    columnHelper.accessor('filing_date', {
      header: 'Filed',
      cell: (info) => info.getValue() ?? '-'
    }),
    columnHelper.accessor('final_score', {
      header: 'Similarity',
      cell: (info) => `${Math.round(info.getValue() * 100)}`
    }),
    columnHelper.display({
      id: 'review',
      header: 'Review',
      cell: (info) => (
        <ReviewControls
          caseId={caseId}
          result={info.row.original}
          canEditReview={canEditReview}
          onReviewUpdated={onReviewUpdated}
        />
      )
    }),
    columnHelper.display({
      id: 'compare',
      header: '',
      cell: (info) => (
        <button
          className="iconButton"
          type="button"
          title="Compare images"
          aria-label={`Compare ${info.row.original.trademark_serial_number}`}
          onClick={() => setSelectedResultId(info.row.original.id)}
        >
          <Columns2 size={16} aria-hidden="true" />
        </button>
      )
    }),
    columnHelper.accessor('source_record_url', {
      header: '',
      cell: (info) => {
        const url = info.getValue()
        return url ? (
          <a className="iconLink" href={url} target="_blank" rel="noreferrer" aria-label="Open USPTO">
            <ExternalLink size={16} aria-hidden="true" />
          </a>
        ) : null
      }
    })
  ]
  const table = useReactTable({
    data: visibleResults,
    columns,
    getCoreRowModel: getCoreRowModel()
  })

  return (
    <section className="resultsPanel" aria-label="Screening results">
      <div className="resultToolbar">
        <label>
          <span>Status</span>
          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(toStatusFilter(event.target.value))}
          >
            <option value="all">All</option>
            <option value="live">LIVE</option>
            <option value="dead">DEAD</option>
          </select>
        </label>
        <label>
          <span>Review</span>
          <select
            value={reviewFilter}
            onChange={(event) => setReviewFilter(toReviewFilter(event.target.value))}
          >
            <option value="all">All</option>
            <option value="review">Review</option>
            <option value="related">Related</option>
            <option value="unrelated">Unrelated</option>
          </select>
        </label>
        <label>
          <span>Sort</span>
          <select value={sortMode} onChange={(event) => setSortMode(toSortMode(event.target.value))}>
            <option value="rank">Rank</option>
            <option value="score">Similarity</option>
            <option value="status">Status</option>
            <option value="filing_date">Filing date</option>
          </select>
        </label>
        <span className="resultCount">
          {visibleResults.length} / {results.length}
        </span>
      </div>
      <ResultComparison result={selectedResult} />
      <div className="tableFrame">
        <table>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

type ResultComparisonProps = {
  result: SearchResult | null
}

function ResultComparison({ result }: ResultComparisonProps) {
  const [overlay, setOverlay] = useState(false)
  const [opacity, setOpacity] = useState(55)
  const [zoom, setZoom] = useState(100)
  const [rotation, setRotation] = useState(0)
  const [grayscale, setGrayscale] = useState(false)
  const imageTransform = `scale(${zoom / 100}) rotate(${rotation}deg)`
  const imageFilter = grayscale ? 'grayscale(1)' : 'none'

  if (!result) {
    return (
      <div className="comparePanel">
        <p className="compareEmpty">No result selected</p>
      </div>
    )
  }

  return (
    <div className="comparePanel">
      <div className="compareHeader">
        <div>
          <span className="compareEyebrow">Compare</span>
          <strong>{result.trademark_serial_number}</strong>
        </div>
        <div className="compareControls">
          <label className="toggleControl">
            <input
              type="checkbox"
              checked={overlay}
              onChange={(event) => setOverlay(event.target.checked)}
            />
            <span>Overlay</span>
          </label>
          <label>
            <span>Opacity</span>
            <input
              type="range"
              min="15"
              max="100"
              value={opacity}
              disabled={!overlay}
              onChange={(event) => setOpacity(Number(event.target.value))}
            />
          </label>
          <label>
            <span>Zoom</span>
            <input
              type="range"
              min="75"
              max="180"
              value={zoom}
              onChange={(event) => setZoom(Number(event.target.value))}
            />
          </label>
          <label>
            <span>Rotate</span>
            <input
              type="range"
              min="-180"
              max="180"
              step="15"
              value={rotation}
              onChange={(event) => setRotation(Number(event.target.value))}
            />
          </label>
          <label className="toggleControl">
            <input
              type="checkbox"
              checked={grayscale}
              onChange={(event) => setGrayscale(event.target.checked)}
            />
            <span>B/W</span>
          </label>
          <button
            className="iconButton"
            type="button"
            title="Reset comparison"
            aria-label="Reset comparison"
            onClick={() => {
              setOverlay(false)
              setOpacity(55)
              setZoom(100)
              setRotation(0)
              setGrayscale(false)
            }}
          >
            <RotateCcw size={16} aria-hidden="true" />
          </button>
        </div>
      </div>
      {overlay ? (
        <div className="overlayStage">
          <CompareImage
            src={result.query_image_url}
            alt="Uploaded query"
            transform={imageTransform}
            filter={imageFilter}
          />
          <CompareImage
            src={result.trademark_image_url}
            alt="USPTO trademark"
            transform={imageTransform}
            filter={imageFilter}
            opacity={opacity / 100}
          />
        </div>
      ) : (
        <div className="compareGrid">
          <ComparePane
            title="Query"
            src={result.query_image_url}
            alt="Uploaded query"
            transform={imageTransform}
            filter={imageFilter}
          />
          <ComparePane
            title="USPTO"
            src={result.trademark_image_url}
            alt="USPTO trademark"
            transform={imageTransform}
            filter={imageFilter}
          />
        </div>
      )}
      <ResultDetails result={result} />
    </div>
  )
}

type ResultDetailsProps = {
  result: SearchResult
}

function ResultDetails({ result }: ResultDetailsProps) {
  return (
    <div className="resultDetails" aria-label="Selected result details">
      <div>
        <span className="compareEyebrow">Score breakdown</span>
        <div className="scoreGrid">
          <ScoreMetric label="Final" value={result.final_score} />
          <ScoreMetric label="Visual" value={result.visual_score} />
          <ScoreMetric label="Text" value={result.text_score} />
          <ScoreMetric label="Class" value={result.class_score} />
          <ScoreMetric label="Status" value={result.status_score} />
          <ScoreMetric label="Hash" value={result.hash_score} />
        </div>
      </div>
      <dl className="metadataGrid">
        <MetadataItem label="Serial" value={result.trademark_serial_number} />
        <MetadataItem label="Registration" value={result.registration_number} />
        <MetadataItem label="Mark" value={result.mark_literal ?? 'Design mark'} />
        <MetadataItem label="Owner" value={result.owner_name} />
        <MetadataItem label="Status" value={result.status_label} />
        <MetadataItem label="Classes" value={result.international_classes.join(', ')} />
        <MetadataItem label="Filed" value={formatDate(result.filing_date)} />
        <MetadataItem label="Registered" value={formatDate(result.registration_date)} />
        <MetadataItem label="Source Updated" value={formatDateTime(result.source_updated_at)} />
        <MetadataItem
          label="Review"
          value={formatReview(result.review_state, result.review_note)}
        />
        <MetadataItem
          className="metadataFull"
          label="Goods/services"
          value={result.goods_services}
        />
      </dl>
    </div>
  )
}

type ScoreMetricProps = {
  label: string
  value: number
}

function ScoreMetric({ label, value }: ScoreMetricProps) {
  return (
    <div>
      <span>{label}</span>
      <strong>{formatScore(value)}</strong>
    </div>
  )
}

type MetadataItemProps = {
  label: string
  value: string | null
  className?: string
}

function MetadataItem({ label, value, className }: MetadataItemProps) {
  return (
    <div className={className}>
      <dt>{label}</dt>
      <dd>{value || '-'}</dd>
    </div>
  )
}

type ComparePaneProps = {
  title: string
  src: string | null
  alt: string
  transform: string
  filter: string
}

function ComparePane({ title, src, alt, transform, filter }: ComparePaneProps) {
  return (
    <div className="comparePane">
      <span>{title}</span>
      <CompareImage src={src} alt={alt} transform={transform} filter={filter} />
    </div>
  )
}

type CompareImageProps = {
  src: string | null
  alt: string
  transform: string
  filter: string
  opacity?: number
}

function CompareImage({ src, alt, transform, filter, opacity = 1 }: CompareImageProps) {
  if (!src) {
    return <div className="missingImage">Image unavailable</div>
  }
  return (
    <img
      className="compareImage"
      src={src}
      alt={alt}
      style={{ filter, opacity, transform }}
    />
  )
}

function filterResults(
  results: SearchResult[],
  statusFilter: StatusFilter,
  reviewFilter: ReviewFilter
): SearchResult[] {
  return results.filter((result) => {
    if (statusFilter !== 'all' && statusKind(result.status_label) !== statusFilter) {
      return false
    }
    if (reviewFilter !== 'all' && result.review_state !== reviewFilter) {
      return false
    }
    return true
  })
}

function sortResults(results: SearchResult[], sortMode: SortMode): SearchResult[] {
  const sorted = [...results]
  if (sortMode === 'score') {
    sorted.sort((left, right) => right.final_score - left.final_score || left.rank - right.rank)
    return sorted
  }
  if (sortMode === 'status') {
    sorted.sort(
      (left, right) =>
        left.status_label.localeCompare(right.status_label) ||
        right.final_score - left.final_score ||
        left.rank - right.rank
    )
    return sorted
  }
  if (sortMode === 'filing_date') {
    sorted.sort(
      (left, right) =>
        dateValue(right.filing_date) - dateValue(left.filing_date) || left.rank - right.rank
    )
    return sorted
  }
  sorted.sort((left, right) => left.rank - right.rank)
  return sorted
}

function statusKind(statusLabel: string): StatusFilter {
  return statusLabel.trim().toUpperCase().startsWith('LIVE') ? 'live' : 'dead'
}

function dateValue(value: string | null): number {
  if (!value) {
    return 0
  }
  const parsed = Date.parse(value)
  return Number.isNaN(parsed) ? 0 : parsed
}

function formatScore(value: number): string {
  return `${Math.round(value * 100)}`
}

function formatDate(value: string | null): string | null {
  if (!value) {
    return null
  }
  const parsed = Date.parse(value)
  if (Number.isNaN(parsed)) {
    return value
  }
  return new Date(parsed).toLocaleDateString()
}

function formatDateTime(value: string | null): string | null {
  if (!value) {
    return null
  }
  const parsed = Date.parse(value)
  if (Number.isNaN(parsed)) {
    return value
  }
  return new Date(parsed).toLocaleString()
}

function formatReview(reviewState: ReviewState, note: string | null): string {
  if (!note) {
    return reviewState
  }
  return `${reviewState}: ${note}`
}

function toStatusFilter(value: string): StatusFilter {
  return value === 'live' || value === 'dead' ? value : 'all'
}

function toReviewFilter(value: string): ReviewFilter {
  return isReviewState(value) ? value : 'all'
}

function toSortMode(value: string): SortMode {
  if (value === 'score' || value === 'status' || value === 'filing_date') {
    return value
  }
  return 'rank'
}

type ReviewControlsProps = {
  caseId: string | null
  result: SearchResult
  canEditReview: boolean
  onReviewUpdated: () => void
}

function ReviewControls({
  caseId,
  result,
  canEditReview,
  onReviewUpdated
}: ReviewControlsProps) {
  const [reviewState, setReviewState] = useState<ReviewState>(result.review_state)
  const [note, setNote] = useState(result.review_note ?? '')

  useEffect(() => {
    setReviewState(result.review_state)
    setNote(result.review_note ?? '')
  }, [result.id, result.review_state, result.review_note])

  const mutation = useMutation({
    mutationFn: updateResultReview,
    onSuccess: onReviewUpdated
  })

  const submitReview = (nextState: ReviewState, nextNote: string) => {
    if (!caseId || !canEditReview) {
      return
    }
    const checkedCaseId = caseId
    mutation.mutate({
      caseId: checkedCaseId,
      resultId: result.id,
      review: {
        review_state: nextState,
        review_note: nextNote.trim() || null
      }
    })
  }

  const handleStateChange = (value: string) => {
    if (!isReviewState(value)) {
      return
    }
    setReviewState(value)
    submitReview(value, note)
  }

  const saveNote = () => {
    const currentNote = result.review_note ?? ''
    if (note.trim() === currentNote.trim()) {
      return
    }
    submitReview(reviewState, note)
  }

  return (
    <div className="reviewControls">
      <select
        className="reviewSelect"
        value={reviewState}
        disabled={!caseId || !canEditReview || mutation.isPending}
        aria-label={`Review state for ${result.trademark_serial_number}`}
        onChange={(event) => handleStateChange(event.target.value)}
      >
        <option value="review">Review</option>
        <option value="related">Related</option>
        <option value="unrelated">Unrelated</option>
      </select>
      <input
        className="reviewNoteInput"
        value={note}
        disabled={!caseId || !canEditReview || mutation.isPending}
        aria-label={`Review note for ${result.trademark_serial_number}`}
        placeholder="Note"
        onBlur={saveNote}
        onChange={(event) => setNote(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.currentTarget.blur()
          }
        }}
      />
      {mutation.isPending ? <span className="reviewSaving">Saving</span> : null}
    </div>
  )
}

function isReviewState(value: string): value is ReviewState {
  return value === 'related' || value === 'unrelated' || value === 'review'
}
